import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  confirmRestaurantCancellation,
  createRestaurantMealSpaceReservation,
  deleteRestaurantMealSpaceReservationDetail,
  fetchRestaurantCancellationHistory,
  fetchRestaurantOverview,
  updateRestaurantColumnSetting,
  updateRestaurantMealMemo,
  updateRestaurantMealSpaceReservationDetail,
} from '@/apis/aipass'
import type {
  DisplayColumnSetting,
  RestaurantCancellationConfirmRequest,
  RestaurantMealSpaceSelection,
  RestaurantOverviewQuery,
} from '@/models/restaurant-manager'
import type { RootState } from '../store'

export const reloadOverview = createAsyncThunk('restaurantManager/reloadOverview', async (query: RestaurantOverviewQuery) => {
  return fetchRestaurantOverview(query)
})

export const reloadCancellation = createAsyncThunk('restaurantManager/reloadCancellation', async () => {
  const response = await fetchRestaurantCancellationHistory()
  return response
})

export const reloadAll = createAsyncThunk('restaurantManager/reloadAll', async (query: RestaurantOverviewQuery, { dispatch }) => {
  const result = await dispatch(reloadOverview(query))
  if (reloadOverview.fulfilled.match(result)) {
    await dispatch(reloadCancellation())
  }
})

// 書き込み系は成功時に必要な再取得だけを実行する。
// エラー、ローディングなどは各コンポーネント側で制御するため、stateは持たない
export const saveMealMemo = createAsyncThunk(
  'restaurantManager/saveMealMemo',
  async (payload: { reservationId: string; memo: string }, { dispatch, getState }) => {
    await updateRestaurantMealMemo({
      reservationId: payload.reservationId,
      memo: payload.memo.trim() === '' ? null : payload.memo,
    })
    const { restaurantManager } = getState() as RootState
    await dispatch(reloadOverview(restaurantManager.query)).unwrap()
  },
)

export const saveColumnSetting = createAsyncThunk(
  'restaurantManager/saveColumnSetting',
  async (displayColumns: DisplayColumnSetting[], { dispatch, getState }) => {
    await updateRestaurantColumnSetting({ displayColumns })
    const { restaurantManager } = getState() as RootState
    await dispatch(reloadOverview(restaurantManager.query)).unwrap()
  },
)

export const confirmCancellation = createAsyncThunk(
  'restaurantManager/confirmCancellation',
  async (payload: RestaurantCancellationConfirmRequest, { dispatch }) => {
    await confirmRestaurantCancellation(payload)
    await dispatch(reloadCancellation()).unwrap()
  },
)

type SaveMealSpaceReservationPayload = {
  reservationId: string
  // 既存の予約行を更新するときは spaceUsageDetailId を指定する。null は新規作成。
  spaceUsageDetailId: string | null
  selection: RestaurantMealSpaceSelection
}

export const saveMealSpaceReservation = createAsyncThunk(
  'restaurantManager/saveMealSpaceReservation',
  async (payload: SaveMealSpaceReservationPayload, { dispatch, getState }) => {
    if (payload.spaceUsageDetailId == null) {
      await createRestaurantMealSpaceReservation({
        reservationId: payload.reservationId,
        spaceId: payload.selection.spaceId,
        useDateStart: payload.selection.useDateStart,
        useDateEnd: payload.selection.useDateEnd,
      })
    } else {
      await updateRestaurantMealSpaceReservationDetail({
        spaceUsageDetailId: payload.spaceUsageDetailId,
        spaceId: payload.selection.spaceId,
        useDateStart: payload.selection.useDateStart,
        useDateEnd: payload.selection.useDateEnd,
      })
    }
    const { restaurantManager } = getState() as RootState
    await dispatch(reloadOverview(restaurantManager.query)).unwrap()
  },
)

export const deleteMealSpaceReservationDetail = createAsyncThunk(
  'restaurantManager/deleteMealSpaceReservationDetail',
  async (payload: { spaceUsageDetailId: string }, { dispatch, getState }) => {
    await deleteRestaurantMealSpaceReservationDetail({ spaceUsageDetailId: payload.spaceUsageDetailId })
    const { restaurantManager } = getState() as RootState
    await dispatch(reloadOverview(restaurantManager.query)).unwrap()
  },
)
